import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isAuthenticated, clearTokens, getLogoutUrl } from '../services/auth';
import { api, ApiError } from '../services/api';
import type { User, Tenant } from '@traaker/shared';

interface AuthState {
  loading: boolean;
  user: User | null;
  tenant: Tenant | null;
  needsOnboarding: boolean;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  loading: true,
  user: null,
  tenant: null,
  needsOnboarding: false,
  logout: () => {},
  refreshProfile: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const loadProfile = async () => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const data = await api.get<{ user: User; tenant: Tenant }>('/users/me');
      setUser(data.user);
      setTenant(data.tenant);
      setNeedsOnboarding(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // 401 = token valid (passed Cognito) but no user record in DynamoDB
        // → needs onboarding
        setNeedsOnboarding(true);
      } else {
        // Actual auth failure or network error → clear session
        clearTokens();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const logout = () => {
    clearTokens();
    window.location.href = getLogoutUrl();
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        user,
        tenant,
        needsOnboarding,
        logout,
        refreshProfile: loadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
